// Kartla ödəniş axını (KT-91), terminal adapterindən asılı deyil (simulyator, .exe agenti, bulud API).
// Qayda: cəhd terminala getməzdən ƏVVƏL serverdə yazılır (pending). Cavab gəlmədisə (vaxt bitdi, əlaqə kəsildi) məbləğ
// təkrar göndərilmir — terminaldan bu əməliyyatın statusu soruşulur. Terminal tanımırsa (null) pul çıxmayıb — rədd; status da
// alınmırsa — needs_review (menecer bankın çıxarışına baxıb həll edir). Kart nömrəsi heç yerə yazılmır.
import type { CardTxnReport, CardTxnResult, CardTxnRow, CardTxnStart, PosAdapter } from '@kassa/shared';

export class PosTimeout extends Error {}

// Terminala göndərilmiş cəhdlər (səhifə yenilənsə də): bu id ilə təkrar «pending» gələndə məbləğ YENİDƏN göndərilmir —
// nəticə serverə çatmayıb, terminaldan status soruşulur (real ECR təkrarı süzməyə bilər — kart iki dəfə çəkilməsin)
const SENT_KEY = 'kassa.pos.sent';

function sentIds(): string[] {
  try {
    const v = JSON.parse(window.sessionStorage.getItem(SENT_KEY) ?? '[]') as unknown;
    return Array.isArray(v) ? (v as string[]) : [];
  } catch {
    return [];
  }
}

function markSent(id: string): void {
  try {
    window.sessionStorage.setItem(SENT_KEY, JSON.stringify([...sentIds().slice(-50), id]));
  } catch (e) {
    console.warn('kassa.pos: göndərilmiş cəhd qeydi saxlanmadı', e instanceof Error ? e.message : e);
  }
}

/** Test üçün: yaddaşsız mühitdə «göndərildi» qeydi. */
const memSent = new Set<string>();

export interface PosApi {
  posStart(input: CardTxnStart): Promise<CardTxnRow>;
  posReport(id: string, r: CardTxnReport): Promise<CardTxnRow>;
}

function report(r: CardTxnResult): CardTxnReport {
  return { ...r };
}

export async function payByCard(
  api: PosApi,
  adapter: PosAdapter,
  input: { id: string; terminalId: string; registerId: string | null; amount: string },
): Promise<CardTxnRow> {
  // Eyni id ilə təkrar (məs. ekran yenidən açıldı) — server eyni sətri qaytarır; qətidirsə terminala getmirik
  const started = await api.posStart({ ...input, kind: 'sale', origTxnId: null });
  if (started.status === 'approved' || started.status === 'declined' || started.status === 'needs_review') return started;
  const alreadySent = memSent.has(input.id) || sentIds().includes(input.id);
  if (started.status === 'pending' && !alreadySent) {
    memSent.add(input.id);
    markSent(input.id); // göndərməzdən ƏVVƏL
    let res: CardTxnResult;
    try {
      res = await adapter.sale({ txnId: input.id, amount: input.amount });
    } catch (e) {
      // Pul baxımından: cavab yoxdur = bilinmir (status soruşulacaq). Səbəb loga (PAN yoxdur — adapter xətası)
      if (!(e instanceof PosTimeout)) console.error('kassa.pos: adapter xətası', e instanceof Error ? e.message.slice(0, 200) : e);
      res = { status: 'unknown', message: 'Terminal cavab vermədi' };
    }
    const row = await api.posReport(input.id, report(res));
    if (row.status !== 'unknown') return row;
  } else if (started.status === 'pending') {
    // Göndərilib, nəticə serverə çatmayıb — təkrar göndərmə yox
    await api.posReport(input.id, { status: 'unknown', message: 'Nəticə yazılmamışdı — status soruşulur' });
  }
  // unknown: təkrar göndərmə yox — status sorğusu
  let st: CardTxnResult | null;
  try {
    st = await adapter.status(input.id);
  } catch {
    return api.posReport(input.id, { status: 'needs_review', message: 'Terminalın cavabı və statusu alınmadı' });
  }
  if (st === null) return api.posReport(input.id, { status: 'declined', message: 'Terminal əməliyyatı almayıb (pul çıxmayıb)' });
  if (st.status === 'unknown') return api.posReport(input.id, { status: 'needs_review', message: st.message ?? 'Status bilinmir' });
  return api.posReport(input.id, report(st));
}
